/**
 * WebsiteViewerPage — public website preview opened in a new tab.
 *
 * External pages: accessible without Twasta authentication.
 * Internal pages: require a valid Twasta login (redirects to / if not).
 *
 * Rendering reuses the production smart-page renderer (SmartPageRuntime,
 * mode='preview') so every block type renders exactly as it does inside
 * the designer's Live Preview — no separate renderer to keep in sync.
 *
 * URL: /site/:userId/:project/:siteSlug
 *      /site/:userId/:project/:siteSlug/:pageSlug
 *
 * Or, when the site is published at a domain of its own (www.acme.com), the
 * host names it and the path is free to be just the page: `/` and `/{page}`.
 * `identity` carries what the path no longer says — see `config/hostBinding`.
 */

import { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Loader2, AlertTriangle, Menu, X, Lock, ChevronDown } from 'lucide-react'
import {
  getSiteManifest, getSitePage, resolveSitePath,
  siteWhoami, siteLogin, siteRegister, siteLogout, siteChangePassword, siteUploadAvatar,
  getSiteToken, setSiteToken,
  siteIdentify, siteQuickRegister, siteForgotPassword, siteSetPasswordFromLink, siteAuthErrorMessage,
  siteBootstrapMemory, siteSaveMemory, siteListMemory, siteForgetMemory, siteMfaSignInApi,
  type SiteManifest, type SitePageEntry, type SiteMenuItem, type SiteUser, type SiteAuthInfo,
} from '@/api/website'
import UserMemoryGate from '@/components/userMemory/UserMemoryGate'
import { trackPageView } from '@twasta/page-runtime/activity'
import { startActivity } from '@/activity/hostTracker'
import ProjectSettingsModal from '@/components/userMemory/ProjectSettingsModal'
import { SecurityContextChip, type PromptVariable } from '@twasta/page-runtime/userMemory'
import type { PageModel } from '@/pages/designers/page/types'
import { resolveTheme, themeCssVars, backgroundCss, chromeBackgroundCss, CONTENT_WIDTH_PX, pageRuntimeCss, pagePaddingXCss } from '@/pages/designers/page/theme'
import { SiteLinkContext, SiteMenuContext, SiteMenusContext, resolveSiteMenus, matchSitePage, AuthMenuContext, PopupLauncher, type AuthMenuAdapter } from '@twasta/page-runtime/renderers'
import { useHeaderScroll, resolveHeaderScroll } from '@twasta/page-runtime/autoHide'
import type { PopupTrigger, PopupModalPosition, PopupAnimation } from '@twasta/page-schema'
import { UserMenu, makeSiteShellApi, siteIdentity, platformNotify } from '@/components/runtime-shell'
import type { ShellCapabilities } from '@/components/runtime-shell'
import SmartPageRuntime from '@/pages/workbench/smartpage-preview/SmartPageRuntime'
import { SiteAuthContext, type SiteAuthAdapter } from '@/pages/workbench/smartpage-preview/SiteAuthContext'
import { SetPasswordPanel, type IdentifyAdapter } from '@twasta/page-runtime/identifyStep'
import { MFA_LIGHT_VARS, MfaSignInStep, parkedStep, promptMfaSignIn, type MfaStep } from '@twasta/page-runtime/shell'
import { BlockChromeContext, SectionChromeContext } from '@/pages/workbench/smartpage-preview/sectionChrome'
import { useSiteEditor } from './edit/useSiteEditor'

// Walk a served page model collecting each block's page-bound embed token
// (embed blocks carry it on `embed._embedToken`; file_upload blocks on the
// block itself). Descends sections + nested block containers.
function collectEmbedTokens(model: PageModel): Record<string, string> {
  const out: Record<string, string> = {}
  const visit = (nodes: unknown): void => {
    if (!Array.isArray(nodes)) return
    for (const n of nodes) {
      if (!n || typeof n !== 'object') continue
      const b = n as {
        id?: string; type?: string; _embedToken?: string
        embed?: { _embedToken?: string }
        blocks?: unknown; children?: unknown; columns?: unknown; items?: unknown
      }
      const tok = b.embed?._embedToken || b._embedToken
      if (b.id && tok) out[b.id] = tok
      visit(b.blocks); visit(b.children); visit(b.columns); visit(b.items)
    }
  }
  const m = model as unknown as { blocks?: unknown; sections?: Array<{ blocks?: unknown }> }
  visit(m.blocks)
  for (const sec of m.sections || []) visit(sec?.blocks)
  return out
}

/** Which site this is, when the URL does not say (a bound domain). */
export interface SiteIdentity {
  userId: string
  project: string
  siteSlug: string
}

export default function WebsiteViewerPage(
  { authPage = false, identity }: { authPage?: boolean; identity?: SiteIdentity } = {},
) {
  const params = useParams<{
    userId: string
    project: string
    siteSlug: string
    pageSlug?: string
    '*'?: string
  }>()
  // On its own domain the site is the whole host: it lives at the root, and
  // the first path segment is the PAGE rather than the owner.
  const hostMode = !!identity
  const userId = identity?.userId ?? params.userId ?? ''
  const project = identity?.project ?? params.project ?? ''
  const siteSlug = identity?.siteSlug ?? params.siteSlug ?? ''
  // One segment arrives as `pageSlug`, a deeper path (a blog post) as the
  // splat. Trailing slashes are how the publisher writes URLs; drop them.
  const pageSlug = (params.pageSlug ?? params['*'] ?? '').replace(/^\/+|\/+$/g, '')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // On the dedicated /login page, `next` is the slug of the gated page the
  // visitor was trying to reach. We forward them there after sign-in. Empty
  // string means "the site home".
  const nextSlug = searchParams.get('next') || ''
  // `?mode=register` opens the sign-in page on its create-account form (the
  // "Create account" button of an embed's sign-in card, an auth_menu Sign up).
  const loginMode: 'login' | 'register' =
    searchParams.get('mode') === 'register' ? 'register' : 'login'
  // `?pu_reset=<token>` on the sign-in page: the set-password / reset link a
  // quick-registered (or forgetful) visitor was sent. Shown before any form.
  const resetToken = authPage ? (searchParams.get('pu_reset') || '') : ''
  const [resetDone, setResetDone] = useState(false)

  const [manifest, setManifest] = useState<SiteManifest | null>(null)
  const [activePage, setActivePage] = useState<SitePageEntry | null>(null)
  const [pageModel, setPageModel] = useState<PageModel | null>(null)
  // Identity of the page whose model is ON SCREEN — set together with
  // `pageModel`, never from `activePage`. `activePage` changes the moment a
  // menu link is clicked, while the previous page stays visible until the next
  // one has loaded; keying the runtime on it would remount the OLD page first.
  const [pageKey, setPageKey] = useState('')
  // Bumped per loadPage call so a slow response for a page the visitor has
  // already left can never replace the page they navigated to.
  const loadSeqRef = useRef(0)
  // Site-wide chrome models (designed header/footer Smart Pages), fetched
  // once per manifest. null = none configured → fall back to auto nav /
  // default footer.
  const [headerModel, setHeaderModel] = useState<PageModel | null>(null)
  const [footerModel, setFooterModel] = useState<PageModel | null>(null)
  // Site-wide pop-up Smart Page (shown in a modal triggered on every page).
  const [popupModel, setPopupModel] = useState<PageModel | null>(null)
  // Per-page chrome override replacements (navigation.headerPageObjName /
  // footerPageObjName). null = inherit the site-wide chrome above.
  const [overrideHeaderModel, setOverrideHeaderModel] = useState<PageModel | null>(null)
  const [overrideFooterModel, setOverrideFooterModel] = useState<PageModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [pageLoading, setPageLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [navOpen, setNavOpen] = useState(false)
  const [siteSettingsOpen, setSiteSettingsOpen] = useState(false)
  // Context-based security on the site: the value (e.g. Financial Entity) a
  // signed-in visitor's rights follow, when that variable is also asked on the
  // website. Shown beside the user menu with one click to switch.
  const [siteSecurityVar, setSiteSecurityVar] = useState<PromptVariable | null>(null)

  // End-user (site visitor) auth — distinct from the Twasta platform login.
  // status 'loading' → roles undefined (fail-open while hydrating, avoids a
  // flash of role-hidden content); 'anonymous' → roles [] (enforce role rules).
  const [authStatus, setAuthStatus] = useState<'loading' | 'authenticated' | 'anonymous'>('loading')
  const [siteUser, setSiteUser] = useState<SiteUser | null>(null)
  // Ref to loadPage so auth callbacks can re-load the current page without a
  // declaration-order / dependency cycle (loadPage is defined further down).
  const loadPageRef = useRef<((entry: SitePageEntry) => void) | null>(null)
  const activePageRef = useRef<SitePageEntry | null>(null)
  useEffect(() => { activePageRef.current = activePage }, [activePage])

  const roles = authStatus === 'loading' ? undefined : (siteUser?.roles ?? [])

  // Header scroll behaviour (static / sticky / auto-hide). Resolved from the
  // site spec's `header_scroll`, falling back to the legacy `header_auto_hide`.
  // One callback ref is attached to whichever header renders (designed chrome
  // OR the auto nav bar) — it binds the moment that element mounts, even though
  // it only appears after the manifest loads.
  const headerScroll = resolveHeaderScroll(manifest?.header_scroll, manifest?.header_auto_hide)
  const headerRef = useHeaderScroll<HTMLElement>(headerScroll)

  // Stable so the runtime doesn't re-resolve data sources every render.
  const siteDataAccess = useMemo(
    () => ({ mode: 'site' as const, userId, project }),
    [userId, project],
  )

  // Every in-site link is built on this. Empty on a bound domain, where the
  // site's home IS `/` — `navigate('')` would be a no-op, so `siteHome` is
  // what the "go to the home page" navigations use.
  const siteBase = hostMode
    ? ''
    : `/site/${encodeURIComponent(userId)}/${encodeURIComponent(project)}/${encodeURIComponent(siteSlug)}`
  const siteHome = siteBase || '/'
  // Resolve site-internal "/slug" links (authored in chrome custom_html,
  // nav buttons, notepad) to this preview's base path so they navigate
  // within the running site instead of escaping to the server root. Matches
  // the link to a real page (tolerating shortened slugs) so "/about" lands
  // on the page whose real slug is "concert-x1-about".
  const sitePages = manifest?.pages
  const siteLinkValue = useMemo(() => ({
    resolveHref: (h: string) => {
      if (!h || !h.startsWith('/') || h.startsWith('//')) return h
      // On the platform an href already under the site's own prefix is
      // final. On a bound domain there is no prefix to recognise — every
      // site-relative href is already in the site's own space — so the test
      // is skipped rather than matching everything.
      if (siteBase && (h === siteBase || h.startsWith(`${siteBase}/`))) return h
      const pg = matchSitePage(h, sitePages)
      if (pg) return pg.is_home ? siteHome : `${siteBase}/${encodeURIComponent(pg.slug)}`
      return `${siteBase}${h}`
    },
    pageHref: (objName: string) => {
      const pg = (sitePages || []).find((p) => p.page_obj_name === objName)
      return pg ? (pg.is_home ? '/' : `/${pg.slug}`) : null
    },
  }), [siteBase, siteHome, sitePages])

  // Route the visitor to the dedicated sign-in page, remembering where they
  // were headed so we can forward them there after a successful login.
  const goToLogin = useCallback((targetSlug?: string, mode?: 'login' | 'register') => {
    setNavOpen(false)
    const params = new URLSearchParams()
    if (targetSlug) params.set('next', targetSlug)
    if (mode === 'register') params.set('mode', 'register')
    const qs = params.toString()
    navigate(`${siteBase}/login${qs ? `?${qs}` : ''}`)
  }, [siteBase, navigate])

  // Advisory pre-emption: route a known-gated page straight to /login instead
  // of letting the page fetch 401 first (avoids a flash + an extra request).
  // Mirrors the backend rule — only internal/document pages are gated, and
  // only when the project requires login. We pre-empt solely when we're
  // certain the visitor is anonymous; while auth is still hydrating we let the
  // request go and rely on the 401 fallback. Older manifests without an
  // explicit `kind` also fall through to the backend's authoritative gate.
  const requireLogin = !!manifest?.auth?.requireLogin
  const needsLogin = useCallback(
    (entry?: SitePageEntry | null): boolean =>
      !!entry && requireLogin && authStatus === 'anonymous'
      && !!entry.kind && entry.kind !== 'external',
    [requireLogin, authStatus],
  )

  // Hydrate the site session: capture a "?token=" handed back by the Twasta
  // sign-in flow, then resolve the current bearer (local session OR twasta
  // token) via whoami. Runs once per site.
  useEffect(() => {
    if (!userId || !project) return
    let cancelled = false
    // Capture a token from the "Sign in with Twasta" redirect, then strip it.
    const params = new URLSearchParams(window.location.search)
    const handoff = params.get('token')
    if (handoff) {
      setSiteToken(userId, project, handoff)
      params.delete('token')
      const qs = params.toString()
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''))
    }
    if (!getSiteToken(userId, project)) {
      setAuthStatus('anonymous')
      return
    }
    setAuthStatus('loading')
    siteWhoami(userId, project)
      .then((res) => {
        if (cancelled) return
        if (res.data.superseded_by_builder) {
          // A leftover application sign-in with no role here, while the
          // caller builds the app: drop it so the preview (and every data
          // read on it) runs as the builder, then re-load the page.
          console.info('[site] role-less application session dropped — previewing as the builder')
          setSiteToken(userId, project, null)
          setSiteUser(null)
          setAuthStatus('anonymous')
          if (activePageRef.current) loadPageRef.current?.(activePageRef.current)
          return
        }
        setSiteUser(res.data)
        setAuthStatus('authenticated')
      })
      .catch(() => {
        if (cancelled) return
        setSiteToken(userId, project, null) // stale/invalid token
        setSiteUser(null)
        setAuthStatus('anonymous')
      })
    return () => { cancelled = true }
  }, [userId, project])

  // Clear the local site session (no server call) and re-run access checks for
  // the current page. Used after a logout and as the user-menu's logout hook.
  const clearSession = useCallback(() => {
    setSiteToken(userId, project, null)
    setSiteUser(null)
    setAuthStatus('anonymous')
    if (activePage) loadPageRef.current?.(activePage)
  }, [userId, project, activePage])

  // ShellApi + identity for the top-bar user menu (self-service + admin). The
  // menu's logout calls siteLogout then clearSession via this adapter.
  const shellApi = useMemo(
    () => makeSiteShellApi(userId, project, clearSession),
    [userId, project, clearSession],
  )
  const shellIdentity = useMemo(() => siteIdentity(siteUser), [siteUser])
  const shellCaps: ShellCapabilities = useMemo(
    () => ({ users: true, access: true, dataAdminUrl: null }),
    [],
  )

  // Called by the login form on success: persist token + hydrate. On the
  // dedicated /login page a separate effect forwards the visitor to `next`;
  // for an inline `auth_form` block on a normal page we re-load the current
  // page so its now-unlocked / role-gated content appears.
  const handleAuthed = useCallback((token: string, user: SiteUser) => {
    setSiteToken(userId, project, token)
    setSiteUser(user)
    setAuthStatus('authenticated')
    if (!authPage && activePage) loadPageRef.current?.(activePage)
  }, [userId, project, activePage, authPage])

  const startTwastaSignin = useCallback((signinUrl: string) => {
    const sep = signinUrl.includes('?') ? '&' : '?'
    window.location.href = `${signinUrl}${sep}redirect_uri=${encodeURIComponent(window.location.href)}`
  }, [])

  // Wires `auth_form` blocks (on a branded login page or anywhere) to the real
  // end-user login flow. Provided to SmartPageRuntime via SiteAuthContext.
  const twastaSigninUrl = manifest?.auth?.twastaSigninUrl || null
  const siteAuthAdapter = useMemo<SiteAuthAdapter>(() => ({
    onSubmit: async (mode, { username, password, email, phone, name }) => {
      let res
      try {
        res = mode === 'register'
          ? await siteRegister(userId, project, username, password, email, phone, name)
          : await siteLogin(userId, project, username, password)
      } catch (err) {
        // The form shows this message as-is: the server's reason, not axios's.
        throw new Error(siteAuthErrorMessage(err, mode === 'register'
          ? 'Your account could not be created — please try again.'
          : 'Sign-in failed — please try again.'))
      }
      // Two-factor sign-in: the code (or setup) in an overlay; the session
      // starts when it is done. Checked BEFORE the fee case, which is the
      // other answer that carries no token.
      const step = parkedStep(res.data)
      if (step) {
        const done = await promptMfaSignIn(step, siteMfaSignInApi(userId, project))
        if (done?.token) handleAuthed(done.token, done.user)
        return
      }
      // A paid registration returns an order instead of a session. The embedded
      // auth_form block has no room for a payment step, so the user is sent to
      // the app's own sign-in page, where ProjectAuthModal drives it properly.
      if (!res.data.token) {
        toast('This application charges an access fee — finishing sign-up on the sign-in page.')
        // The application's own sign-in page: its root on a bound domain,
        // the tenant-less Run link on the platform (which redirects).
        window.location.href = hostMode
          ? '/?pu_login=1'
          : `/run/${encodeURIComponent(project)}?pu_login=1`
        return
      }
      handleAuthed(res.data.token, res.data.user)
    },
    twastaSignin: twastaSigninUrl ? () => startTwastaSignin(twastaSigninUrl) : undefined,
  }), [userId, project, hostMode, handleAuthed, twastaSigninUrl, startTwastaSignin])

  // Identify-first sign-in (page-runtime `IdentifyStep`): email OR mobile
  // number → log in, or create the account on the spot and get a session. Put
  // on the AuthMenuContext adapter, so an `auth_form` in `mode: "identify"` and
  // any block asking the host to render the step (a checkout) find it there.
  const identifyAdapter = useMemo<IdentifyAdapter>(() => {
    const fail = (err: unknown, fallback: string): never => {
      throw new Error(siteAuthErrorMessage(err, fallback))
    }
    return {
      identify: async (identifier) => {
        try { return (await siteIdentify(userId, project, identifier)).data }
        catch (err) { return fail(err, 'We could not check that — please try again.') }
      },
      quickRegister: async (identifier, name, contact) => {
        let res
        try { res = await siteQuickRegister(userId, project, identifier, name, siteSlug || undefined, contact) }
        catch (err) { return fail(err, 'Your account could not be created — please try again.') }
        const d = res.data
        if (!d.token) {
          return { setPasswordSent: !!d.set_password_sent, channel: d.set_password_channel, paymentRequired: true }
        }
        handleAuthed(d.token, d.user)
        return { setPasswordSent: !!d.set_password_sent, channel: d.set_password_channel }
      },
      login: async (identifier, password) => {
        let res
        try { res = await siteLogin(userId, project, identifier, password) }
        catch (err) { return fail(err, 'Sign-in failed — please try again.') }
        const step = parkedStep(res.data)
        if (step) {
          const done = await promptMfaSignIn(step, siteMfaSignInApi(userId, project))
          if (done?.token) handleAuthed(done.token, done.user)
          return
        }
        handleAuthed(res.data.token, res.data.user)
      },
      forgotPassword: async (identifier) => {
        try { await siteForgotPassword(userId, project, identifier, siteSlug || undefined) }
        catch (err) { fail(err, 'We could not send a link — please try again.') }
      },
    }
  }, [userId, project, siteSlug, handleAuthed])

  // Wires `auth_menu` blocks (header/footer auth widget) to the live session:
  // signed-out → route to the sign-in page; signed-in → avatar dropdown with
  // self-service account (change password + profile-image upload) and logout.
  // Provided to SmartPageRuntime via the package's AuthMenuContext.
  const allowRegistration = !!manifest?.auth?.allowRegistration
  const authMenuAdapter = useMemo<AuthMenuAdapter>(() => ({
    status: authStatus,
    user: siteUser
      ? {
          // A phone-only account's username is a reserved placeholder key
          // (it has no inbox, so `email` comes back null): show its number.
          username: siteUser.full_name
            || (siteUser.email === null && siteUser.phone ? siteUser.phone : siteUser.username),
          email: siteUser.email,
          roles: siteUser.roles || [],
          isAdmin: siteUser.is_admin || (siteUser.roles || []).includes('admin'),
          avatarUrl: siteUser.avatar_url ?? null,
        }
      : null,
    onLogin: () => goToLogin(activePage?.slug || ''),
    onSignup: allowRegistration ? () => goToLogin(activePage?.slug || '', 'register') : undefined,
    onLogout: async () => {
      try { await siteLogout(userId, project) } catch { /* idempotent */ }
      clearSession()
    },
    changePassword: async (oldPw, newPw) => { await siteChangePassword(userId, project, oldPw, newPw) },
    uploadAvatar: async (file) => {
      const res = await siteUploadAvatar(userId, project, file)
      setSiteUser(res.data.user)
      return res.data.avatar_url
    },
    identify: identifyAdapter,
  }), [authStatus, siteUser, allowRegistration, activePage, userId, project, goToLogin, clearSession, identifyAdapter])

  // On the dedicated /login page, once the visitor is authenticated — whether
  // via the local form, an inline auth_form, or the "Sign in with Twasta"
  // round-trip (which lands back here with a session) — forward them to the
  // page they originally wanted (`next`), or the site home. `replace` keeps
  // the login URL out of history so Back doesn't bounce them through it again.
  useEffect(() => {
    if (!authPage || authStatus !== 'authenticated') return
    navigate(nextSlug ? `${siteBase}/${encodeURIComponent(nextSlug)}` : siteHome, { replace: true })
  }, [authPage, authStatus, nextSlug, siteBase, siteHome, navigate])

  // Resolve the page theme the same way the designer canvas does, so the
  // `--pg-*` CSS variables, fonts, background and content width all match
  // the Live Preview. resolveTheme tolerates an undefined theme.
  const resolvedTheme = useMemo(() => resolveTheme(pageModel?.theme), [pageModel?.theme])
  const contentMaxWidth = resolvedTheme.layout.maxWidth
    || CONTENT_WIDTH_PX[resolvedTheme.layout.contentWidth]

  // Top-nav menu: prefer the manifest's hierarchical `menu` (pages +
  // folder groups → dropdown submenus). Fall back to a flat list built
  // from `pages` for older manifests that predate the menu field.
  const menuItems: SiteMenuItem[] = useMemo(() => {
    if (manifest?.menu && manifest.menu.length > 0) return manifest.menu
    return (manifest?.pages || [])
      .filter((p) => p.show_in_nav)
      .map((p) => ({ type: 'page', page_obj_name: p.page_obj_name, slug: p.slug, label: p.nav_label }) as SiteMenuItem)
  }, [manifest])
  // The site's NAMED menus, resolved once against this manifest's pages. With
  // a `header` / `footer` menu defined, the automatic nav bar and default
  // footer show those instead of the whole nav tree.
  const siteMenus = useMemo(
    () => resolveSiteMenus(manifest?.menus, manifest?.pages),
    [manifest?.menus, manifest?.pages],
  )
  const headerItems: SiteMenuItem[] = siteMenus.header || menuItems
  const footerItems: SiteMenuItem[] = siteMenus.footer || menuItems

  // The public viewer must look like a real website, not the (dark) app
  // shell. Force the light theme on the body so globals.css variables
  // resolve to their light values regardless of the user's app theme.
  useEffect(() => {
    const prev = document.body.className
    document.body.className = 'light-theme'
    return () => { document.body.className = prev }
  }, [])

  // Load site manifest once on mount
  useEffect(() => {
    if (!userId || !project || !siteSlug) return
    setLoading(true)
    setError(null)
    getSiteManifest(userId, project, siteSlug)
      .then((res) => {
        setManifest(res.data)
        document.title = res.data.title || siteSlug
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Site not found')
      })
      .finally(() => setLoading(false))
  }, [userId, project, siteSlug])

  // Make changes re-reads the manifest (settings, menu, pages, theme) without
  // the whole-page loader; the header / footer are fetched again with it.
  const [siteRev, setSiteRev] = useState(0)
  useEffect(() => {
    if (!siteRev || !userId || !project || !siteSlug) return
    getSiteManifest(userId, project, siteSlug)
      .then((res) => setManifest(res.data))
      .catch((err) => console.warn('[site] manifest reload failed', err))
  }, [siteRev, userId, project, siteSlug])

  // Resolve which page entry to display based on URL slug. Skipped on the
  // /login page, which renders the sign-in experience rather than a page.
  useEffect(() => {
    if (authPage || !manifest) return
    const home = manifest.pages.find((p) => p.is_home) || manifest.pages[0]
    const plain = pageSlug
      ? manifest.pages.find((p) => p.slug === pageSlug && !p.collection)
      : null
    // Anything that is not a plain page — a collection's list page (it needs
    // its pagination), a record, a category/author page, a moved URL — is
    // what the PUBLISHER produces, so the server resolves it by path.
    const entry: SitePageEntry | undefined = !pageSlug ? home
      : plain || {
        slug: pageSlug, page_obj_name: '', nav_label: '', show_in_nav: false,
        is_home: false, position: 0, resolvePath: pageSlug,
      }
    setActivePage(entry || null)
  }, [authPage, manifest, pageSlug])

  // Activity analytics: this site's visitors. A signed-in visitor sends their
  // site session so the server can name them; everyone else is known by the
  // first-party visitor id. One page view per page the visitor lands on.
  useEffect(() => {
    if (!userId || !project) return
    startActivity({
      realm: 'site', surface: 'site_viewer', owner: userId, project,
      headers: (): Record<string, string> => {
        const token = getSiteToken(userId, project)
        return token ? { Authorization: `Bearer ${token}` } : {}
      },
    })
  }, [userId, project])
  useEffect(() => {
    if (authPage || !activePage) return
    trackPageView(activePage.page_obj_name || activePage.slug || 'home', {
      obj_type: 'S', title: activePage.nav_label || undefined,
    })
  }, [authPage, activePage])

  // Fetch the designed header/footer Smart Pages (if configured). They're
  // kind='external' (public), so getSitePage never 401s here. Best-effort:
  // a missing chrome page just falls back to the auto nav / default footer.
  useEffect(() => {
    if (authPage || !manifest || !userId || !project || !siteSlug) return
    let cancelled = false
    const fetchChrome = (name: string | null | undefined, set: (m: PageModel | null) => void) => {
      if (!name) { set(null); return }
      getSitePage(userId, project, siteSlug, name)
        .then((res) => {
          if (cancelled) return
          const raw = res.data as unknown as { page?: PageModel } & PageModel
          set((raw.page ?? raw) as PageModel)
        })
        .catch(() => { if (!cancelled) set(null) })
    }
    fetchChrome(manifest.header_page_obj_name, setHeaderModel)
    fetchChrome(manifest.footer_page_obj_name, setFooterModel)
    fetchChrome(manifest.popup_page_obj_name, setPopupModel)
    return () => { cancelled = true }
  }, [authPage, manifest, userId, project, siteSlug, siteRev])

  // Per-page chrome replacement: when the active page sets
  // navigation.headerPageObjName / footerPageObjName, fetch those chrome
  // pages to swap in for this page only. Cleared when the page has none.
  // Keyed on the override NAMES, not the page model: moving between pages that
  // share a header (or have none) must not drop and re-fetch it, which
  // flashed the site-wide header in between.
  const pageNavNames = (pageModel?.navigation || {}) as { headerPageObjName?: string; footerPageObjName?: string }
  const overrideHeaderName = pageNavNames.headerPageObjName
  const overrideFooterName = pageNavNames.footerPageObjName
  useEffect(() => {
    if (!userId || !project || !siteSlug) {
      setOverrideHeaderModel(null); setOverrideFooterModel(null); return
    }
    let cancelled = false
    const fetchOverride = (name: string | undefined, set: (m: PageModel | null) => void) => {
      if (!name) { set(null); return }
      getSitePage(userId, project, siteSlug, name)
        .then((res) => {
          if (cancelled) return
          const raw = res.data as unknown as { page?: PageModel } & PageModel
          set((raw.page ?? raw) as PageModel)
        })
        .catch(() => { if (!cancelled) set(null) })
    }
    fetchOverride(overrideHeaderName, setOverrideHeaderModel)
    fetchOverride(overrideFooterName, setOverrideFooterModel)
    return () => { cancelled = true }
  }, [userId, project, siteSlug, overrideHeaderName, overrideFooterName])

  // Effective chrome for the active page: disable (showHeader/showFooter ===
  // false) or replace (override models) the site-wide chrome. Themes resolve
  // off the effective model so a dark header renders dark, etc.
  const pageNav = (pageModel?.navigation || {}) as {
    showHeader?: boolean; showFooter?: boolean
    headerPageObjName?: string; footerPageObjName?: string
  }
  const hideHeader = pageNav.showHeader === false
  const hideFooter = pageNav.showFooter === false
  const effHeaderModel = hideHeader ? null : (overrideHeaderModel ?? headerModel)
  const effFooterModel = hideFooter ? null : (overrideFooterModel ?? footerModel)
  const headerTheme = useMemo(() => resolveTheme(effHeaderModel?.theme), [effHeaderModel?.theme])
  const footerTheme = useMemo(() => resolveTheme(effFooterModel?.theme), [effFooterModel?.theme])

  // Load page model when active page changes. The API returns the raw
  // page-object file shape `{ page: PageModel }`; we unwrap to the inner
  // model that SmartPageRuntime expects.
  const loadPage = useCallback(async (entry: SitePageEntry) => {
    if (!userId || !project || !siteSlug) return
    const seq = ++loadSeqRef.current
    const stale = () => seq !== loadSeqRef.current
    // The current page stays on screen until the next one is ready — blanking
    // it to a spinner on every menu click is what made navigation flicker.
    setPageLoading(true)
    try {
      // Forward the browser URL's query params (minus the site-auth handoff
      // token) so the page's server-side beforeLoad event sees them as
      // url_params — e.g. the ?key= of a key-gated public form.
      const urlParams = Object.fromEntries(
        new URLSearchParams(window.location.search),
      )
      delete urlParams.token
      let res
      if (entry.resolvePath) {
        const resolved = await resolveSitePath(userId, project, siteSlug, entry.resolvePath, urlParams)
        if (stale()) return
        if (resolved.data.redirect) {
          const to = resolved.data.redirect.replace(/^\/+|\/+$/g, '')
          navigate(to ? `${siteBase}/${to}/` : siteHome, { replace: true })
          return
        }
        res = { data: resolved.data.page as Record<string, unknown> }
      } else {
        res = await getSitePage(userId, project, siteSlug, entry.page_obj_name, urlParams)
        if (stale()) return
      }
      const raw = res.data as unknown as { page?: PageModel } & PageModel
      const model = (raw.page ?? raw) as PageModel
      // Before-load access decision: the server runs the page's `beforeLoad`
      // event and, when it denies / redirects, attaches `__before_load__`.
      // Honour it BEFORE rendering — otherwise the gated page would still show.
      const directive = (model as {
        __before_load__?: {
          denied?: boolean
          redirect?: { kind: string; url?: string; page?: string }
          message?: string
        }
      }).__before_load__
      if (directive && (directive.denied || directive.redirect)) {
        const r = directive.redirect
        if (r?.kind === 'external' && r.url) {
          window.location.assign(r.url)
          return
        }
        if (r?.kind === 'page' && r.page) {
          const target = manifest?.pages.find((p) => p.page_obj_name === r.page)
          if (target) {
            navigate(
              target.slug ? `${siteBase}/${encodeURIComponent(target.slug)}` : siteHome,
              { replace: true },
            )
            return
          }
        }
        // Denied with no resolvable redirect → surface the access message.
        setError(directive.message || 'You do not have access to this page.')
        return
      }
      setPageModel(model)
      setPageKey(entry.resolvePath || entry.page_obj_name || model.obj_name)
      document.title = entry.seo_title || (model.seo as { title?: string } | undefined)?.title || model.title || manifest?.title || siteSlug
    } catch (err: unknown) {
      if (stale()) return
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 401) {
        // The page is login-gated (kind='internal'/'document') and the visitor
        // isn't signed in. Route to the dedicated /login page, remembering this
        // page in `next` so we forward them right back here after sign-in.
        // This is the authoritative gate — it also catches cases the advisory
        // pre-emption misses (e.g. auth still hydrating, or older manifests
        // without an explicit page `kind`).
        goToLogin(entry.slug || '')
      } else if (status === 403 && getSiteToken(userId, project)) {
        // The stored application sign-in is refused here (its account holds
        // no role on this application). Drop it and load the page once more
        // without it — public pages then open, gated ones ask to sign in
        // (with an account that has access). No loop: the retry carries no
        // token, so it cannot take this branch again.
        console.info('[site] application sign-in refused here — signed out of it, loading again')
        setSiteToken(userId, project, null)
        setSiteUser(null)
        setAuthStatus('anonymous')
        loadPageRef.current?.(entry)
      } else {
        setError((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to load page')
      }
    } finally {
      if (!stale()) setPageLoading(false)
    }
  }, [userId, project, siteSlug, manifest, goToLogin, navigate, siteBase, siteHome])

  // A newly swapped-in page starts at the top (the old spinner did this by
  // collapsing the document). Layout effect, so the new page is never painted
  // at the previous page's scroll offset. An in-page #anchor is left alone.
  useLayoutEffect(() => {
    if (pageKey && !window.location.hash) window.scrollTo(0, 0)
  }, [pageKey])

  useEffect(() => { loadPageRef.current = loadPage }, [loadPage])

  useEffect(() => {
    if (activePage) loadPage(activePage)
  }, [activePage, loadPage])

  // Re-fetch the current page to obtain fresh page-bound embed tokens (they
  // ride in the page model per embed/file_upload block). Called by an embed
  // when a site-txn call 401s with EMBED_TOKEN_EXPIRED. Updates the model so
  // all blocks re-render with new tokens, and returns the block→token map.
  const refreshEmbedTokens = useCallback(async (): Promise<Record<string, string> | null> => {
    if (!userId || !project || !siteSlug || !activePage) return null
    try {
      const urlParams = Object.fromEntries(new URLSearchParams(window.location.search))
      delete urlParams.token
      const data = activePage.resolvePath
        ? (await resolveSitePath(userId, project, siteSlug, activePage.resolvePath, urlParams)).data.page
        : (await getSitePage(userId, project, siteSlug, activePage.page_obj_name, urlParams)).data
      const raw = (data || {}) as unknown as { page?: PageModel } & PageModel
      const model = (raw.page ?? raw) as PageModel
      setPageModel(model)
      return collectEmbedTokens(model)
    } catch {
      return null
    }
  }, [userId, project, siteSlug, activePage])

  const navigateTo = (entry: SitePageEntry) => goToSlug(entry.slug || '')

  const goToSlug = (slug: string) => {
    setNavOpen(false)
    // Pre-empt a gated destination to the sign-in page so the visitor sees the
    // login screen instead of a flash of "loading" then a redirect.
    const entry = manifest?.pages.find((p) => p.slug === slug)
    if (needsLogin(entry)) { goToLogin(slug); return }
    navigate(slug ? `${siteBase}/${slug.split('/').map(encodeURIComponent).join('/')}` : siteHome)
  }
  // A custom menu link: an outside URL leaves the site, a site path routes
  // through the same resolution every in-page link uses.
  const goToHref = (href: string) => {
    setNavOpen(false)
    if (/^[a-z][a-z0-9+.-]*:/i.test(href) || href.startsWith('//')) {
      window.location.assign(href)
      return
    }
    navigate(siteLinkValue.resolveHref(href))
  }

  // Website Make changes (docs/website_make_changes.md): builders only, on the
  // platform — never on a bound domain, never on the sign-in page.
  const pendingPageRef = useRef<string | null>(null)
  const goToPageObj = (obj: string) => {
    const entry = manifest?.pages.find((p) => p.page_obj_name === obj)
    if (!entry) { pendingPageRef.current = obj; return }
    pendingPageRef.current = null
    goToSlug(entry.is_home ? '' : entry.slug || '')
  }
  useEffect(() => {
    const want = pendingPageRef.current
    if (!want || !manifest) return
    const entry = manifest.pages.find((p) => p.page_obj_name === want)
    if (!entry) return
    pendingPageRef.current = null
    navigate(entry.is_home ? siteHome : `${siteBase}/${(entry.slug || '').split('/').map(encodeURIComponent).join('/')}`)
  }, [manifest, navigate, siteBase, siteHome])
  const editor = useSiteEditor({
    project,
    manifest,
    activeObj: activePage?.page_obj_name,
    wantEdit: !hostMode && !authPage && searchParams.get('make_changes') === '1',
    goToPage: goToPageObj,
    reloadSite: () => setSiteRev((n) => n + 1),
    reloadPage: () => { const a = activePageRef.current; if (a) loadPageRef.current?.(a) },
    onExit: () => {
      if (new URLSearchParams(window.location.search).has('make_changes')) {
        navigate(window.location.pathname, { replace: true })
      }
    },
  })
  // A page's Make changes (the Workbench) opens the mode on that page:
  // `?make_changes=1&page=<obj>` — taken once, when the mode is on.
  const startPageRef = useRef<string | null>(
    searchParams.get('make_changes') === '1' ? searchParams.get('page') : null)
  useEffect(() => {
    const want = startPageRef.current
    if (!want || !manifest || !editor.active) return
    startPageRef.current = null
    if (activePageRef.current?.page_obj_name !== want) goToPageObj(want)
  }, [manifest, editor.active]) // eslint-disable-line react-hooks/exhaustive-deps

  // In-page buttons (e.g. "Book Tickets") fire a `smartpage:navigate`
  // window event via SmartPageRuntime — the embedded runtime has no router
  // of its own. Resolve smart-page (obj_type 'S') targets to a site page
  // and route to its slug. Non-page targets (T/W/D/…) aren't site pages,
  // so we surface a clear message instead of failing silently.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { objType?: string; objName?: string }
        | undefined
      const objName = detail?.objName
      if (!objName) return
      if (detail?.objType && detail.objType !== 'S') {
        // A public site only hosts smart pages; T/D/… can't be opened here.
        // Claim the event so the dispatcher doesn't also fall back.
        e.preventDefault()
        toast.error(`"${objName}" can't be opened from this site.`)
        return
      }
      const target = manifest?.pages.find((p) => p.page_obj_name === objName)
      if (!target) {
        e.preventDefault()
        toast.error(`Page "${objName}" is not part of this site.`)
        return
      }
      e.preventDefault()
      setNavOpen(false)
      // A button linking to a gated (internal) page routes through sign-in,
      // then forwards to that page — the cross-boundary external→internal flow.
      if (needsLogin(target)) { goToLogin(target.slug || ''); return }
      navigate(target.slug ? `${siteBase}/${encodeURIComponent(target.slug)}` : siteHome)
    }
    window.addEventListener('smartpage:navigate', handler)
    return () => window.removeEventListener('smartpage:navigate', handler)
  }, [manifest, siteBase, siteHome, navigate, needsLogin, goToLogin])

  // In-site links rendered BY PAGE BLOCKS — a designed header's `nav_menu`,
  // buttons, the cart, custom HTML — are plain <a href>. Left to the browser,
  // each click is a full document load: the whole site re-boots behind
  // "Loading site…" and re-fetches its manifest and chrome, which is the
  // flicker on menu navigation. Route them through the router instead so only
  // the page body swaps. Bubble phase + `defaultPrevented`, so a block that
  // handles its own click (in-page anchors, drawers) still wins.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
      if (!a || a.hasAttribute('download')) return
      const target = a.getAttribute('target')
      if (target && target !== '_self') return
      const raw = a.getAttribute('href') || ''
      if (!raw || raw.startsWith('#')) return
      let url: URL
      try { url = new URL(a.href, window.location.href) } catch { return }
      if (url.origin !== window.location.origin) return
      const here = window.location
      // Same page, different #anchor: the browser's own scroll is right.
      if (url.pathname === here.pathname && url.search === here.search) return
      // Same page, different query: the page's beforeLoad reads url_params
      // only on load, so let the browser load it for real.
      if (url.pathname === here.pathname) return
      const path = url.pathname.replace(/\/+$/, '')
      // Only this site's own pages. Anything else — another site, the API,
      // the SSO callback, a file (`/x/brochure.pdf`) — stays a real load.
      if (siteBase && path !== siteBase && !path.startsWith(`${siteBase}/`)) return
      if (/^\/(api|sso)(\/|$)/.test(path) || /\.[a-z0-9]{1,5}$/i.test(path)) return
      let slug = path.slice(siteBase.length).replace(/^\/+/, '')
      try { slug = decodeURIComponent(slug) } catch { /* keep the raw slug */ }
      e.preventDefault()
      setNavOpen(false)
      const entry = manifest?.pages.find((p) => p.slug === slug)
      if (needsLogin(entry)) { goToLogin(slug); return }
      navigate(`${url.pathname}${url.search}${url.hash}`)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [manifest, siteBase, navigate, needsLogin, goToLogin])

  if (loading) {
    return (
      <Centered>
        <Loader2 size={36} className="wv-spin" />
        <span>Loading site…</span>
      </Centered>
    )
  }

  if (error) {
    return (
      <Centered>
        <AlertTriangle size={40} color="#ef4444" />
        <h2 style={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', margin: 0 }}>Unable to load site</h2>
        <p style={{ margin: 0 }}>{error}</p>
      </Centered>
    )
  }

  // Dedicated sign-in page (route .../login?next=<slug>). Reached when a
  // visitor tries to open a gated (internal/document) page while signed out.
  // Presents the site's own login experience — a branded login Smart Page if
  // configured, else the built-in form — and handles "Sign in with Twasta".
  // The forwarding effect routes the visitor to `next` once authenticated.
  if (authPage && resetToken && !resetDone) {
    return (
      <Centered>
        <div style={{ width: '100%', maxWidth: 420, padding: '0 16px', boxSizing: 'border-box' }}>
          <SetPasswordPanel
            onSubmit={async (pw) => {
              try { await siteSetPasswordFromLink(userId, project, resetToken, pw) }
              catch (err) { throw new Error(siteAuthErrorMessage(err, 'This link is invalid or has expired.')) }
            }}
          />
          <div style={{ textAlign: 'center', marginTop: 12 }}>
            <button
              type="button"
              onClick={() => {
                setResetDone(true)
                const qs = new URLSearchParams(searchParams)
                qs.delete('pu_reset'); qs.delete('owner')
                navigate(`${siteBase}/login${qs.toString() ? `?${qs}` : ''}`, { replace: true })
              }}
              style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 14, textDecoration: 'underline' }}
            >
              Continue to the site
            </button>
          </div>
        </div>
      </Centered>
    )
  }

  if (authPage) {
    // Show a spinner while the session is still resolving or already valid —
    // only present the sign-in form once the visitor is definitively
    // anonymous. Avoids a flash of the login form for an already-signed-in
    // visitor (who the forwarding effect will route onward).
    if (authStatus !== 'anonymous') {
      return (
        <Centered>
          <Loader2 size={28} className="wv-spin" />
          <span style={{ fontSize: '0.9rem' }}>Signing you in…</span>
        </Centered>
      )
    }
    const destination = nextSlug
      ? manifest?.pages.find((p) => p.slug === nextSlug)
      : null
    // The sign-in page links to other site pages (terms, privacy, partner):
    // it needs the same link resolution as every other page of the site.
    return (
      <SiteLinkContext.Provider value={siteLinkValue}>
      <SiteLoginGate
        userId={userId}
        project={project}
        siteSlug={siteSlug}
        auth={manifest?.auth}
        adapter={siteAuthAdapter}
        authMenuAdapter={authMenuAdapter}
        contentMaxWidth={contentMaxWidth}
        onAuthed={handleAuthed}
        destinationLabel={destination?.nav_label || null}
        initialMode={loginMode}
      />
      </SiteLinkContext.Provider>
    )
  }

  const homeEntry = manifest?.pages.find((p) => p.is_home) || manifest?.pages[0]
  const hasNav = headerItems.length > 0
  // A designed header replaces the auto nav bar; otherwise the auto nav shows.
  const showAutoNav = hasNav && !effHeaderModel && !hideHeader
  const activeObj = activePage?.page_obj_name

  // Render a designed header/footer Smart Page wrapped in its own theme,
  // full-width with a centered inner — matching the SSR ChromePage so the
  // in-app viewer and the static deploy look identical.
  const renderChrome = (
    model: PageModel, theme: ReturnType<typeof resolveTheme>, tag: 'header' | 'footer',
  ) => {
    const Tag = tag
    const innerMax = theme.layout.maxWidth || CONTENT_WIDTH_PX[theme.layout.contentWidth]
    return (
      <Tag
        ref={tag === 'header' ? headerRef : undefined}
        style={{
          width: '100%',
          ...themeCssVars(theme),
          ...chromeBackgroundCss(theme),
        }}
      >
        <div style={{ maxWidth: innerMax, margin: '0 auto', padding: `0 ${pagePaddingXCss(theme.layout.paddingX)}` }}>
          <SiteAuthContext.Provider value={siteAuthAdapter}>
            <AuthMenuContext.Provider value={authMenuAdapter}>
              <SmartPageRuntime
                page={model}
                mode="preview"
                docId={null}
                project={project}
                userRoles={roles}
                dataAccess={siteDataAccess}
                fullBleed
              />
            </AuthMenuContext.Provider>
          </SiteAuthContext.Provider>
        </div>
      </Tag>
    )
  }

  // Dropdown contents for a desktop submenu — recursive so a nested
  // folder renders as a labelled sub-section within the same dropdown.
  const renderDropdownItems = (items: SiteMenuItem[]): React.ReactNode =>
    items.map((it, i) => {
      if (it.type === 'group') {
        return (
          <div key={`g${i}`} style={{ padding: '0.25rem 0' }}>
            <div style={{ padding: '0.25rem 0.75rem', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
              {it.label}
            </div>
            {renderDropdownItems(it.children)}
          </div>
        )
      }
      if (it.type === 'link') {
        return (
          <button
            key={`l${i}`}
            type="button"
            onClick={() => goToHref(it.href)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6, fontWeight: 500, fontSize: '0.875rem', color: '#475569', whiteSpace: 'nowrap' }}
          >
            {it.label}
          </button>
        )
      }
      const isActive = it.page_obj_name === activeObj
      return (
        <button
          key={it.page_obj_name}
          type="button"
          onClick={() => goToSlug(it.slug)}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.5rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer', borderRadius: 6, fontWeight: isActive ? 700 : 500, fontSize: '0.875rem', color: isActive ? 'var(--color-primary, #4f46e5)' : '#475569', whiteSpace: 'nowrap' }}
        >
          {it.label}
        </button>
      )
    })

  // Mobile menu — flat list, folders shown as indented section headers.
  const renderMobileItems = (items: SiteMenuItem[], depth = 0): React.ReactNode =>
    items.map((it, i) => {
      if (it.type === 'group') {
        return (
          <div key={`g${i}`}>
            <div style={{ padding: '0.5rem 1.5rem 0.25rem', paddingLeft: `${1.5 + depth * 0.75}rem`, fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
              {it.label}
            </div>
            {renderMobileItems(it.children, depth + 1)}
          </div>
        )
      }
      if (it.type === 'link') {
        return (
          <button
            key={`l${i}`}
            type="button"
            onClick={() => goToHref(it.href)}
            style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.625rem 1.5rem', paddingLeft: `${1.5 + depth * 0.75}rem`, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, fontSize: '0.9375rem', color: '#475569' }}
          >
            {it.label}
          </button>
        )
      }
      const isActive = it.page_obj_name === activeObj
      return (
        <button
          key={it.page_obj_name}
          type="button"
          onClick={() => goToSlug(it.slug)}
          style={{ display: 'block', width: '100%', textAlign: 'left', padding: '0.625rem 1.5rem', paddingLeft: `${1.5 + depth * 0.75}rem`, background: 'none', border: 'none', cursor: 'pointer', fontWeight: isActive ? 700 : 500, fontSize: '0.9375rem', color: isActive ? 'var(--color-primary, #4f46e5)' : '#475569' }}
        >
          {it.label}
        </button>
      )
    })

  return (
    <SiteLinkContext.Provider value={siteLinkValue}>
    <SiteMenuContext.Provider value={manifest?.menu || []}>
    <SiteMenusContext.Provider value={siteMenus}>
    {/* `clip` (not `hidden`) so a full-bleed band's 100vw never adds a
        sideways scrollbar, while sticky headers/sections keep working. */}
    <div style={{ minHeight: '100vh', background: '#ffffff', color: '#0f172a', overflowX: 'clip' }}>
      {/* The shared page-runtime stylesheet (nav drawer, widgets, chrome).
          The header/footer chrome and the page body render through
          SmartPageRuntime, not ThemedPageShell, so nothing else injects it —
          without it a header nav_menu showed its hidden drawer checkbox. */}
      <style dangerouslySetInnerHTML={{ __html: pageRuntimeCss() }} />
      {editor.bar}
      {/* Ask the signed-in site visitor for any external-flagged prompt
          variables they haven't answered (no-op when anonymous). */}
      {authStatus === 'authenticated' && (
        <UserMemoryGate
          projectId={project}
          surface="site"
          loader={() => siteBootstrapMemory(userId, project, 'site')}
          saver={(values) => siteSaveMemory(userId, project, values)}
        />
      )}
      <SiteSecurityContextLoader
        enabled={authStatus === 'authenticated'}
        load={() => siteListMemory(userId, project, 'site')}
        onLoaded={setSiteSecurityVar}
      />
      {siteSettingsOpen && (
        <ProjectSettingsModal
          projectId={project}
          surface="site"
          onClose={() => setSiteSettingsOpen(false)}
          loader={() => siteListMemory(userId, project, 'site')}
          saver={(values) => siteSaveMemory(userId, project, values)}
          forgetter={(name) => siteForgetMemory(userId, project, name)}
        />
      )}
      {/* Site header: a designed header Smart Page if configured, else the
          auto nav bar built from the manifest menu. Per-page override may
          replace (effHeaderModel) or hide (hideHeader) it. */}
      {editor.frame(<>
      {effHeaderModel && editor.chromeControl('header', renderChrome(effHeaderModel, headerTheme, 'header'))}
      {/* Nav bar */}
      {showAutoNav && editor.chromeControl('header', (
        <nav ref={headerRef} style={{
          // Positioning (sticky / static) is owned by useHeaderScroll so the
          // chosen behaviour applies; we only style the bar's appearance here.
          zIndex: 50,
          background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #e2e8f0',
        }}>
          <div style={{ maxWidth: '72rem', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '1.5rem', height: 60, padding: '0 1.5rem' }}>
            {homeEntry && (
              <button
                type="button"
                onClick={() => navigateTo(homeEntry)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0, padding: 0 }}
              >
                {/* App icon chip — Material icon + accent from application.json. */}
                <span style={{
                  width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                  background: manifest?.themeColor || 'var(--color-primary, #4f46e5)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span className="material-icons" style={{ fontSize: 18, color: '#fff' }}>
                    {manifest?.icon || 'apps'}
                  </span>
                </span>
                <span style={{ fontWeight: 800, fontSize: '1.0625rem', color: 'var(--color-primary, #4f46e5)' }}>
                  {manifest?.title}
                </span>
              </button>
            )}

            {/* Desktop nav — folders become hover dropdown submenus */}
            {/* Right cluster: nav links (desktop), user menu (always), and the
                mobile hamburger — kept together so the user menu stays visible
                at every width. */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <div className="wv-desktop-nav" style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
              {headerItems.map((item, idx) => {
                if (item.type === 'link') {
                  return (
                    <button
                      key={`l${idx}`}
                      type="button"
                      onClick={() => goToHref(item.href)}
                      style={{ padding: '0.375rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, color: '#475569', borderRadius: 6, fontSize: '0.875rem', whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      {item.label}
                    </button>
                  )
                }
                if (item.type === 'group') {
                  const active = subtreeHasActive(item, activeObj)
                  return (
                    <div key={`g${idx}`} className="wv-nav-group" style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        type="button"
                        style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0.375rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer', fontWeight: active ? 700 : 500, color: active ? 'var(--color-primary, #4f46e5)' : '#475569', borderRadius: 6, fontSize: '0.875rem', whiteSpace: 'nowrap' }}
                      >
                        {item.label}
                        <ChevronDown size={14} />
                      </button>
                      <div className="wv-dropdown" style={{ position: 'absolute', top: '100%', left: 0, minWidth: 200, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '0.375rem' }}>
                        {renderDropdownItems(item.children)}
                      </div>
                    </div>
                  )
                }
                const isActive = item.page_obj_name === activeObj
                return (
                  <button
                    key={item.page_obj_name}
                    type="button"
                    onClick={() => goToSlug(item.slug)}
                    style={{
                      padding: '0.375rem 0.75rem', background: 'none', border: 'none', cursor: 'pointer',
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? 'var(--color-primary, #4f46e5)' : '#475569',
                      borderRadius: 6, fontSize: '0.875rem', whiteSpace: 'nowrap', flexShrink: 0,
                    }}
                  >
                    {item.label}
                  </button>
                )
              })}
            </div>{/* /wv-desktop-nav */}

            {shellIdentity && siteSecurityVar && (
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
                <SecurityContextChip
                  label={siteSecurityVar.prompt || siteSecurityVar.name}
                  value={siteSecurityVar.pending ? null : (siteSecurityVar.value ?? null)}
                  valueLabel={(siteSecurityVar.options || []).find(
                    (o) => String(o.value) === String(siteSecurityVar.value))?.label || null}
                  required={siteSecurityVar.pending || !siteSecurityVar.value}
                  onSwitch={() => setSiteSettingsOpen(true)}
                />
              </div>
            )}
            {shellIdentity && (
              <div style={{ display: 'flex', alignItems: 'center', paddingLeft: 6 }}>
                <UserMenu
                  identity={shellIdentity}
                  api={shellApi}
                  caps={shellCaps}
                  accentColor={manifest?.themeColor || '#4f46e5'}
                  notify={platformNotify}
                  extraItems={[{
                    label: 'Project settings',
                    onClick: () => setSiteSettingsOpen(true),
                  }]}
                />
              </div>
            )}

            {/* Mobile hamburger */}
            <button type="button" onClick={() => setNavOpen(!navOpen)} className="wv-mobile-btn" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569' }}>
              {navOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            </div>{/* /right cluster */}
          </div>

          {/* Mobile dropdown */}
          {navOpen && (
            <div className="wv-mobile-nav" style={{ padding: '0.5rem 0 1rem', borderTop: '1px solid #e2e8f0' }}>
              {renderMobileItems(headerItems)}
            </div>
          )}
        </nav>
      ))}

      {/* Page content — rendered by the production smart-page runtime,
          wrapped in the page's resolved theme so `--pg-*` tokens, fonts,
          background and content width match the designer's Live Preview. */}
      <main
        style={{
          minHeight: effHeaderModel || effFooterModel ? 'auto' : (showAutoNav ? 'calc(100vh - 60px)' : '100vh'),
          background: resolvedTheme.palette.background,
          ...themeCssVars(resolvedTheme),
          ...backgroundCss(resolvedTheme.layout.background),
        }}
      >
        {pageLoading && pageModel && <div className="wv-progress" aria-hidden />}
        {pageModel ? (
          <div key={pageKey || pageModel.obj_name} className="wv-page" style={{
            maxWidth: contentMaxWidth,
            margin: '0 auto',
            padding: `${resolvedTheme.layout.paddingY}px ${pagePaddingXCss(resolvedTheme.layout.paddingX)}`,
          }}>
            <SiteAuthContext.Provider value={siteAuthAdapter}>
              <AuthMenuContext.Provider value={authMenuAdapter}>
                <SectionChromeContext.Provider value={editor.sectionChrome}>
                <BlockChromeContext.Provider value={editor.blockChrome}>
                <SmartPageRuntime
                  // One runtime per page: two blog posts share a template, and
                  // a runtime's page variables are seeded only when it mounts.
                  key={pageKey || pageModel.obj_name}
                  page={editor.displayPage(pageModel) ?? pageModel}
                  mode="preview"
                  docId={null}
                  project={project}
                  userRoles={roles}
                  dataAccess={siteDataAccess}
                  refreshEmbedTokens={refreshEmbedTokens}
                  // The site owns the viewport: a `width: "full"` band spans it
                  // (not inside the Make changes phone frame).
                  fullBleed={!editor.phone}
                  viewport={editor.phone ? 'mobile' : undefined}
                />
                </BlockChromeContext.Provider>
                </SectionChromeContext.Provider>
              </AuthMenuContext.Provider>
            </SiteAuthContext.Provider>
          </div>
        ) : pageLoading ? (
          <Centered minHeight="50vh">
            <Loader2 size={28} className="wv-spin" />
            <span style={{ fontSize: '0.9rem' }}>Loading page…</span>
          </Centered>
        ) : (
          <Centered minHeight="40vh">
            <span style={{ color: '#94a3b8' }}>This page has no content yet.</span>
          </Centered>
        )}
        {editor.pageEnd}
      </main>

      {/* Site footer: a designed footer Smart Page if configured, else a
          simple default footer (title + menu links + copyright). Per-page
          override may replace (effFooterModel) or hide (hideFooter) it. */}
      {hideFooter
        ? null
        : editor.chromeControl('footer', effFooterModel
          ? renderChrome(effFooterModel, footerTheme, 'footer')
          : <DefaultFooter menu={footerItems} title={manifest?.title || ''} onNav={goToSlug} onHref={goToHref} />)}
      </>)}
      {editor.overlays}

      {/* Site-wide pop-up: a designed Smart Page shown in a modal triggered
          on every page (config from manifest.popup_config). The page content
          renders through the same runtime as the rest of the site. */}
      {popupModel && (() => {
        const cfg = (manifest?.popup_config || {}) as {
          trigger?: PopupTrigger; modalTitle?: string
          modalSize?: 'sm' | 'md' | 'lg'; modalPosition?: PopupModalPosition; animation?: PopupAnimation
        }
        const trigger: PopupTrigger = cfg.trigger || { style: 'fab', label: 'Open', position: 'bottom-right' }
        const popupTheme = resolveTheme(popupModel.theme)
        return (
          <PopupLauncher
            trigger={trigger}
            modalTitle={cfg.modalTitle}
            modalSize={cfg.modalSize}
            modalPosition={cfg.modalPosition}
            animation={cfg.animation}
            themeVars={themeCssVars(popupTheme) as Record<string, string>}
          >
            <SiteAuthContext.Provider value={siteAuthAdapter}>
              <AuthMenuContext.Provider value={authMenuAdapter}>
                <SmartPageRuntime
                  page={popupModel}
                  mode="preview"
                  docId={null}
                  project={project}
                  userRoles={roles}
                  dataAccess={siteDataAccess}
                />
              </AuthMenuContext.Provider>
            </SiteAuthContext.Provider>
          </PopupLauncher>
        )
      })()}

      <style>{`
        @keyframes wv-spin { to { transform: rotate(360deg); } }
        .wv-spin { animation: wv-spin 1s linear infinite; }
        /* Page swap: the new page eases in over the old one instead of the
           content area blanking to a spinner. */
        @keyframes wv-page-in { from { opacity: 0; } to { opacity: 1; } }
        .wv-page { animation: wv-page-in 160ms ease-out; }
        /* Slow page loads only: a thin bar at the top, held back 200ms so a
           fast navigation shows nothing at all. */
        @keyframes wv-progress-in { to { opacity: 1; } }
        @keyframes wv-progress-run { from { transform: translateX(-100%); } to { transform: translateX(250%); } }
        .wv-progress { position: fixed; top: 0; left: 0; right: 0; height: 2px; z-index: 10000; overflow: hidden; pointer-events: none;
          opacity: 0; animation: wv-progress-in 120ms ease-out 200ms forwards; }
        .wv-progress::after { content: ''; position: absolute; inset: 0; width: 40%;
          background: var(--pg-primary, #2563eb); animation: wv-progress-run 1.1s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wv-page { animation: none; }
          .wv-progress::after { animation: none; width: 100%; }
        }
        .wv-mobile-btn { display: none; }
        .wv-mobile-nav { display: block; }
        .wv-nav-group .wv-dropdown { display: none; }
        .wv-nav-group:hover .wv-dropdown { display: block; }
        .wv-dropdown button:hover { background: #f1f5f9; }
        @media (max-width: 720px) {
          .wv-desktop-nav { display: none !important; }
          .wv-mobile-btn { display: block !important; }
        }
        @media (min-width: 721px) {
          .wv-mobile-nav { display: none !important; }
        }
      `}</style>
    </div>
    </SiteMenusContext.Provider>
    </SiteMenuContext.Provider>
    </SiteLinkContext.Provider>
  )
}

/** True if `item` is, or contains anywhere in its subtree, the active page. */
function subtreeHasActive(item: SiteMenuItem, activeObj?: string): boolean {
  if (item.type === 'page') return item.page_obj_name === activeObj
  if (item.type === 'link') return false
  return item.children.some((c) => subtreeHasActive(c, activeObj))
}

/** Flatten a menu tree to its leaf links (groups carry no own URL). A page
 *  leaf carries its slug, a custom link its href. */
function flattenMenu(items: SiteMenuItem[]): { slug?: string; href?: string; label: string }[] {
  const out: { slug?: string; href?: string; label: string }[] = []
  for (const it of items || []) {
    if (it.type === 'page') out.push({ slug: it.slug, label: it.label })
    else if (it.type === 'link') out.push({ href: it.href, label: it.label })
    else out.push(...flattenMenu(it.children || []))
  }
  return out
}

/** Fallback footer when no footer page is designed: site title, the menu
 *  links, and a copyright line. Mirrors the SSR default footer. */
function DefaultFooter(
  { menu, title, onNav, onHref }: {
    menu: SiteMenuItem[]; title: string; onNav: (slug: string) => void; onHref: (href: string) => void
  },
) {
  const links = flattenMenu(menu)
  if (!title && links.length === 0) return null
  return (
    <footer style={{ borderTop: '1px solid #e2e8f0', marginTop: 48, background: '#ffffff', color: '#475569' }}>
      <div style={{ maxWidth: '72rem', margin: '0 auto', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', textAlign: 'center' }}>
        {title ? <div style={{ fontWeight: 600, color: '#0f172a' }}>{title}</div> : null}
        {links.length ? (
          <nav style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
            {links.map((l, i) => (
              <button
                key={l.href ? `l${i}` : (l.slug || 'home')}
                type="button"
                onClick={() => (l.href ? onHref(l.href) : onNav(l.slug || ''))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', fontSize: '0.875rem', padding: 0 }}
              >
                {l.label}
              </button>
            ))}
          </nav>
        ) : null}
        {title ? <div style={{ opacity: 0.7, fontSize: '0.85rem' }}>© {title}</div> : null}
      </div>
    </footer>
  )
}

function Centered({ children, minHeight = '100vh' }: { children: React.ReactNode; minHeight?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight, flexDirection: 'column', gap: '0.85rem', color: '#64748b', padding: '2rem', textAlign: 'center', background: '#ffffff' }}>
      {children}
    </div>
  )
}

/**
 * Built-in sign-in screen for a login-gated site page. Authenticates the
 * visitor against the project's own end-user store (project-users) and/or hands
 * off to "Sign in with Twasta", exactly as a deployed site would. On success it
 * hands the token + user up so the gated page re-loads.
 */
function SiteLoginGate({
  userId, project, siteSlug, auth, adapter, authMenuAdapter, contentMaxWidth, onAuthed, destinationLabel,
  initialMode = 'login',
}: {
  userId: string
  project: string
  siteSlug: string
  auth?: SiteAuthInfo
  adapter: SiteAuthAdapter
  /** Gives a branded login page's `auth_form` (identify mode) its adapter. */
  authMenuAdapter?: AuthMenuAdapter
  contentMaxWidth: number
  onAuthed: (token: string, user: SiteUser) => void
  /** nav label of the page the visitor was headed to, for friendlier copy. */
  destinationLabel?: string | null
  /** Open on the create-account form (only honoured when registration is on). */
  initialMode?: 'login' | 'register'
}) {
  // Branded login page: when the project points `loginPage` at a smart page
  // (which contains an `auth_form` block), render that page — wired to the real
  // login flow via SiteAuthContext — instead of the built-in form. The backend
  // always serves the login page publicly so the visitor can reach it.
  const loginPage = auth?.loginPage || null
  const [brandedModel, setBrandedModel] = useState<PageModel | null | undefined>(loginPage ? undefined : null)

  useEffect(() => {
    if (!loginPage) { setBrandedModel(null); return }
    let cancelled = false
    setBrandedModel(undefined)
    getSitePage(userId, project, siteSlug, loginPage)
      .then((res) => {
        if (cancelled) return
        const raw = res.data as unknown as { page?: PageModel } & PageModel
        setBrandedModel((raw.page ?? raw) as PageModel)
      })
      .catch(() => { if (!cancelled) setBrandedModel(null) }) // fall back to built-in form
    return () => { cancelled = true }
  }, [loginPage, userId, project, siteSlug])

  if (loginPage && brandedModel === undefined) {
    return (
      <Centered>
        <Loader2 size={28} className="wv-spin" />
        <span style={{ fontSize: '0.9rem' }}>Loading sign-in…</span>
      </Centered>
    )
  }
  if (loginPage && brandedModel) {
    // Wrapped in the login page's OWN resolved theme exactly like a normal
    // page (<main> below): without the `--pg-*` variables its blocks fell
    // back to the platform's UI colours and fonts — dark text on the page's
    // navy panel.
    const loginTheme = resolveTheme(brandedModel.theme)
    return (
      <div style={{
        minHeight: '100vh', background: loginTheme.palette.background,
        ...themeCssVars(loginTheme), ...backgroundCss(loginTheme.layout.background),
      }}>
        <div style={{
          maxWidth: loginTheme.layout.maxWidth || contentMaxWidth, margin: '0 auto',
          padding: `${loginTheme.layout.paddingY}px ${pagePaddingXCss(loginTheme.layout.paddingX)}`,
        }}>
          <SiteAuthContext.Provider value={{ ...adapter, initialMode }}>
            <AuthMenuContext.Provider value={authMenuAdapter ?? null}>
              <SmartPageRuntime page={brandedModel} mode="preview" docId={null} project={project} userRoles={[]} />
            </AuthMenuContext.Provider>
          </SiteAuthContext.Provider>
        </div>
      </div>
    )
  }

  const providers = auth?.providers || ['project-users']
  const allowLocal = providers.includes('project-users') || providers.includes('any')
  const allowTwasta = !!auth?.twastaSigninUrl && (providers.includes('twasta') || providers.includes('any'))
  const allowRegister = !!auth?.allowRegistration && allowLocal

  const [mode, setMode] = useState<'login' | 'register'>(
    initialMode === 'register' && allowRegister ? 'register' : 'login')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [mfaStep, setMfaStep] = useState<MfaStep | null>(null)
  const mfaApi = useMemo(() => siteMfaSignInApi(userId, project), [userId, project])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    setBusy(true)
    try {
      const res = mode === 'register'
        ? await siteRegister(userId, project, username, password, email || undefined)
        : await siteLogin(userId, project, username, password)
      const step = parkedStep(res.data)
      if (step) { setMfaStep(step); return }
      if (!res.data.token) {
        // A fee applies — this inline form cannot host a payment step, so say
        // so plainly rather than appearing to have signed them in.
        setErr('This application charges an access fee. Use the sign-in page to complete your registration.')
        return
      }
      onAuthed(res.data.token, res.data.user)
    } catch (e2: unknown) {
      setErr((e2 as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        || (mode === 'register' ? 'Could not create account' : 'Invalid username or password'))
    } finally {
      setBusy(false)
    }
  }

  const signInWithTwasta = () => {
    if (!auth?.twastaSigninUrl) return
    const redirectUri = window.location.href
    const sep = auth.twastaSigninUrl.includes('?') ? '&' : '?'
    window.location.href = `${auth.twastaSigninUrl}${sep}redirect_uri=${encodeURIComponent(redirectUri)}`
  }

  const input: React.CSSProperties = {
    padding: '0.625rem 0.75rem', border: '1px solid #e2e8f0', borderRadius: 8,
    fontSize: '0.9375rem', width: '100%', boxSizing: 'border-box', color: '#0f172a', background: '#fff',
  }

  return (
    <Centered>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 64, height: 64, borderRadius: '50%', background: '#eef2ff',
      }}>
        <Lock size={28} color="#4f46e5" />
      </div>
      <h2 style={{ fontWeight: 700, fontSize: '1.25rem', color: '#0f172a', margin: 0 }}>
        {mode === 'register' ? 'Create an account' : 'Sign in to continue'}
      </h2>
      <p style={{ margin: 0, maxWidth: 380, lineHeight: 1.6 }}>
        {destinationLabel
          ? `Sign in to continue to “${destinationLabel}”. We'll take you there right after.`
          : "This page requires you to sign in. You'll be brought right back here."}
      </p>

      {allowLocal && mfaStep && (
        <div style={{ ...MFA_LIGHT_VARS, width: '100%', maxWidth: 340, marginTop: 4, textAlign: 'left' }}>
          <MfaSignInStep
            step={mfaStep}
            api={mfaApi}
            onSignedIn={(done) => { setMfaStep(null); onAuthed(done.token, done.user) }}
            onCancel={() => { setMfaStep(null); setPassword('') }}
          />
        </div>
      )}
      {allowLocal && !mfaStep && (
        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 320, marginTop: 4 }}>
          <input style={input} value={username} onChange={(e) => setUsername(e.target.value)}
            placeholder="Username" autoComplete="username" autoFocus />
          {mode === 'register' && (
            <input style={input} type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (optional)" autoComplete="email" />
          )}
          <input style={input} type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Password" autoComplete={mode === 'register' ? 'new-password' : 'current-password'} />
          {err && <div style={{ color: '#dc2626', fontSize: '0.8125rem', textAlign: 'left' }}>{err}</div>}
          <button type="submit" disabled={busy || !username || !password}
            style={{ padding: '0.625rem 1rem', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: busy ? 'default' : 'pointer', fontWeight: 600, fontSize: '0.9375rem', opacity: busy || !username || !password ? 0.6 : 1 }}>
            {busy ? 'Please wait…' : (mode === 'register' ? 'Create account' : 'Sign in')}
          </button>
        </form>
      )}

      {allowRegister && (
        <button type="button" onClick={() => { setErr(null); setMode(mode === 'register' ? 'login' : 'register') }}
          style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '0.875rem' }}>
          {mode === 'register' ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
        </button>
      )}

      {allowTwasta && (
        <>
          {allowLocal && <div style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>or</div>}
          <button type="button" onClick={signInWithTwasta}
            style={{ padding: '0.625rem 1.25rem', borderRadius: 8, background: '#fff', color: '#0f172a', border: '1px solid #e2e8f0', cursor: 'pointer', fontWeight: 600, fontSize: '0.9375rem' }}>
            Sign in with Twasta
          </button>
        </>
      )}
    </Centered>
  )
}


/** Reads the site's security-context variable (if any) once the visitor is
 *  signed in. A component rather than an effect in the page so the page's
 *  own effect order is left exactly as it was. */
function SiteSecurityContextLoader({ enabled, load, onLoaded }: {
  enabled: boolean
  load: () => Promise<PromptVariable[]>
  onLoaded: (v: PromptVariable | null) => void
}) {
  useEffect(() => {
    if (!enabled) { onLoaded(null); return }
    let cancelled = false
    load()
      .then((list) => { if (!cancelled) onLoaded(list.find((x) => x.security_context) || null) })
      .catch(() => { if (!cancelled) onLoaded(null) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])
  return null
}
